import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, CircularProgress, Tooltip } from "@mui/material";
import { functions, httpsCallable } from "../firebaseConfig";

const localStorageCodeKey = "itsConnect.loginHotpCode";
const localStorageIssuedKey = "itsConnect.loginHotpIssuedAt";

const formatCode = (code) => {
  if (!code) return "";
  const trimmed = code.toString().trim();
  if (trimmed.length === 4) {
    const normalized = trimmed.padStart(4, "0");
    return normalized;
  }
  if (trimmed.length === 6) {
    const normalized = trimmed.padStart(6, "0");
    return `${normalized.slice(0, 3)} ${normalized.slice(3)}`;
  }
  return trimmed;
};

const LoginCodeDisplay = ({ currentStaffData, theme }) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const issueLoginCodeCallable = useMemo(
    () => httpsCallable(functions, "issueLoginCode"),
    []
  );

  const persistLocally = useCallback((nextCode, nextIssuedAt) => {
    try {
      if (typeof window === "undefined") return;
      if (nextCode) {
        window.localStorage.setItem(localStorageCodeKey, nextCode);
      } else {
        window.localStorage.removeItem(localStorageCodeKey);
      }
      if (nextIssuedAt) {
        window.localStorage.setItem(localStorageIssuedKey, nextIssuedAt);
      } else {
        window.localStorage.removeItem(localStorageIssuedKey);
      }
    } catch (storageError) {
      console.warn("Failed to persist login code locally:", storageError);
    }
  }, []);

  useEffect(() => {
    const hasStaffData =
      currentStaffData && Object.keys(currentStaffData).length > 0;

    if (hasStaffData) {
      if (currentStaffData.loginHotpCode) {
        setCode(currentStaffData.loginHotpCode);
        persistLocally(
          currentStaffData.loginHotpCode,
          currentStaffData.loginHotpIssuedAt || ""
        );
      } else {
        setCode("");
        persistLocally("", "");
      }
      setError("");
      return;
    }

    try {
      if (typeof window === "undefined") return;
      const storedCode = window.localStorage.getItem(localStorageCodeKey);
      if (storedCode) {
        setCode(storedCode);
      }
    } catch (storageError) {
      console.warn("Failed to read stored login code:", storageError);
    }
  }, [currentStaffData, persistLocally]);

  const handleRequestCode = async () => {
    setLoading(true);
    setError("");

    try {
      const result = await issueLoginCodeCallable();
      const response = result?.data || {};

      if (!response.code) {
        throw new Error(response.error || "No code returned.");
      }

      setCode(response.code);
      const issued = response.issuedAt || "";
      persistLocally(response.code, issued);
    } catch (requestError) {
      console.error("Error requesting login code:", requestError);
      setError(
        requestError?.message || "Unable to fetch a login code right now."
      );
    } finally {
      setLoading(false);
    }
  };

  const hasCode = Boolean(code && code.trim());

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      {hasCode ? (
        <span
          style={{
            fontWeight: "bold",
            letterSpacing: 6,
            fontSize: 20,
            color: theme === "dark" ? "#ffffff" : "#1f1f1f",
          }}
        >
          {formatCode(code)}
        </span>
      ) : null}
      {!hasCode ? (
        <Tooltip title="Generate a login code.">
          <span>
            <Button
              variant="outlined"
              size="small"
              onClick={handleRequestCode}
              disabled={loading}
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                borderWidth: 2,
                paddingX: 2.5,
                paddingY: 0.75,
              }}
            >
              {loading ? <CircularProgress size={16} color="inherit" /> : "Get Code"}
            </Button>
          </span>
        </Tooltip>
      ) : null}
      {error ? (
        <span
          style={{
            fontSize: 12,
            color: theme === "dark" ? "#ff8a80" : "#d32f2f",
          }}
        >
          {error}
        </span>
      ) : null}
    </div>
  );
};

export default LoginCodeDisplay;
